import React from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  ModelEntityGroupDto,
  SpoolUpdateForm,
  UnitSystem,
  useBulkUpdateSpoolsMutation,
  useGetModelEntityGroupsQuery
} from 'providers/api';
import { i18n } from 'i18n';
import { distanceImperialToMetric, distanceMetricToImperial } from 'utils/unitConversions';
import PCSpool from '../../../MineModel/Spools/PCSpool';
import { spoolDisplayName, spoolEndsLabel } from '../../../MineModel/Spools/utils';
import { Model } from '../../../hooks/useModelState';
import { TOOLBAR_MENU_Z_INDEX } from './utils';

type DisplayUnit = 'metric' | 'imperial';

interface FlowLoopLengthsPopoutProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  model: Model;
}

// A flow loop is a length of pipe coiled into an edge, so a row is a SPOOL, labelled by its name or the two
// nodes it joins. Its pipe is the spool's own pipe — there is nothing left to resolve.
interface FlowLoopRow {
  spoolId: string;
  groupName: string;
  spoolName: string;
  endsLabel: string;
  loopMetres: number;
  pipeName: string;
  pipeColour: string;
}

const UNGROUPED_SORT_KEY = String.fromCharCode(0xffff);
const COMMIT_DEBOUNCE_MS = 600;

const formatLength = (metres: number, unit: DisplayUnit): { value: string; suffix: string } => {
  const value = unit === 'imperial' ? distanceMetricToImperial(metres) : metres;
  return { value: value.toFixed(0), suffix: unit === 'imperial' ? 'ft' : 'm' };
};

const buildGroupNameByNodeId = (groups: ModelEntityGroupDto[]): Record<string, string> => groups
  .filter((group) => group.groupType === 'Node')
  .reduce<Record<string, string>>((acc, group) => {
    const name = group.groupName ?? '';
    return (group.memberIds ?? []).reduce(
      (inner, id) => ({ ...inner, [id]: inner[id] ? `${inner[id]}, ${name}` : name }),
      acc,
    );
  }, {});

const chokedSpoolIds = (model: Model): string[] => Object.values(model.spools)
  .filter((spool) => spool.chokeLength > 0)
  .map((spool) => spool.spoolId);

// Rows are fixed for the lifetime of the popout. Filtering the live chokeLength here instead would delete the
// row - and the flow loop with it - the moment a typed value passed through 0: clearing the field to type
// "0.5" commits 0 on the first keystroke, the row unmounts mid-edit and the debounced flush persists 0.
const buildRows = (model: Model, groups: ModelEntityGroupDto[], spoolIds: string[]): FlowLoopRow[] => {
  const groupNameByNodeId = buildGroupNameByNodeId(groups);
  return spoolIds
    .map((spoolId) => model.spools[spoolId])
    .filter((spool): spool is PCSpool => !!spool)
    .map((spool) => ({
      spoolId: spool.spoolId,
      groupName: groupNameByNodeId[spool.pointA.id] ?? groupNameByNodeId[spool.pointB.id] ?? '',
      spoolName: spoolDisplayName(spool),
      endsLabel: spoolEndsLabel(spool),
      loopMetres: spool.chokeLength,
      pipeName: spool.pipe?.name ?? 'Unknown',
      pipeColour: spool.pipe?.colour ?? '#888888',
    }))
    .sort((a, b) => (a.groupName || UNGROUPED_SORT_KEY).localeCompare(b.groupName || UNGROUPED_SORT_KEY)
      || a.spoolName.localeCompare(b.spoolName));
};

const FlowLoopLengthsPopout = ({
  open,
  anchorEl,
  onClose,
  model,
}: FlowLoopLengthsPopoutProps) => {
  const mineModelId = model.project.entityId;
  const { data: groups } = useGetModelEntityGroupsQuery(mineModelId, { enabled: !!mineModelId });
  const bulkUpdateSpoolsMutation = useBulkUpdateSpoolsMutation(mineModelId);
  const displayUnit: DisplayUnit = model.project?.unitSystemPreference === UnitSystem.Imperial ? 'imperial' : 'metric';
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [rowSpoolIds, setRowSpoolIds] = React.useState<string[]>([]);
  const [wasOpen, setWasOpen] = React.useState(false);
  const pendingRef = React.useRef<Map<string, number>>(new Map());
  const flushTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Snapshot which spools carry a loop as the popout opens, not on every render. useModelState hands back a
  // fresh model object each render, so a live filter would re-evaluate on every keystroke.
  if (open !== wasOpen) {
    setWasOpen(open);
    setRowSpoolIds(open ? chokedSpoolIds(model) : []);
  }

  const rows = React.useMemo(() => buildRows(model, groups ?? [], rowSpoolIds), [model, groups, rowSpoolIds]);

  // Every pending edit goes out in ONE patch: the reticulation document is one per mine, so a request
  // per row would race itself.
  const flushPending = () => {
    const pending = pendingRef.current;
    if (pending.size === 0) return;
    const forms: SpoolUpdateForm[] = Array.from(pending.entries()).map(([spoolId, chokeLength]) => ({ spoolId, chokeLength }));
    pendingRef.current = new Map();
    bulkUpdateSpoolsMutation.mutate({ projectId: mineModelId, spools: forms });
  };

  const handleLengthChange = (spoolId: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [spoolId]: value }));

    const entered = parseFloat(value);
    if (!Number.isFinite(entered)) return;

    const metres = displayUnit === 'imperial' ? distanceImperialToMetric(entered) : entered;
    model.setSpoolProperties(spoolId, { chokeLength: metres });
    pendingRef.current.set(spoolId, metres);

    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushPending, COMMIT_DEBOUNCE_MS);
  };

  const rowToLine = (row: FlowLoopRow): string => {
    const { value, suffix } = formatLength(row.loopMetres, displayUnit);
    return [row.groupName, row.spoolName, `${value} ${suffix}`, row.pipeName].join('\t');
  };

  const handleCopyRow = (row: FlowLoopRow) => {
    navigator.clipboard.writeText(rowToLine(row));
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(rows.map(rowToLine).join('\n'));
  };

  React.useEffect(() => {
    if (open) return;
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    setDrafts({});
    flushPending();
  }, [open]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ zIndex: TOOLBAR_MENU_Z_INDEX }}
    >
      <Box sx={{ p: 1.5, minWidth: 420, maxWidth: 620 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">{i18n._('Flow Loop Lengths')}</Typography>
          {rows.length > 0 && (
            <Tooltip title={i18n._('Copy to clipboard')}>
              <Button
                size="small"
                color="secondary"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={handleCopyAll}
                sx={{ textTransform: 'none' }}
              >
                {i18n._('Copy all')}
              </Button>
            </Tooltip>
          )}
        </Box>
        <Divider />
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {i18n._('No flow loops in model')}
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 360, overflow: 'auto', mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n._('Group')}</TableCell>
                  <TableCell>{i18n._('Spool')}</TableCell>
                  <TableCell align="right">{i18n._('Loop Length')}</TableCell>
                  <TableCell>{i18n._('Pipe')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const { value, suffix } = formatLength(row.loopMetres, displayUnit);
                  return (
                    <TableRow key={row.spoolId}>
                      <TableCell sx={{ color: row.groupName ? 'text.primary' : 'text.disabled' }}>
                        {row.groupName || '—'}
                      </TableCell>
                      <TableCell>
                        {row.spoolName}
                        {row.spoolName !== row.endsLabel && (
                          <Typography variant="caption" color="text.secondary" component="div">{row.endsLabel}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          color="secondary"
                          value={drafts[row.spoolId] ?? value}
                          onChange={(e) => handleLengthChange(row.spoolId, e.target.value)}
                          inputProps={{ style: { textAlign: 'right' } }}
                          sx={{ width: 110 }}
                          helperText={suffix}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '2px',
                              backgroundColor: row.pipeColour,
                              border: '1px solid',
                              borderColor: 'divider',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.pipeName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell padding="none" align="right">
                        <Tooltip title={i18n._('Copy to clipboard')}>
                          <IconButton size="small" onClick={() => handleCopyRow(row)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default FlowLoopLengthsPopout;
